import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import LoginSlideshow from '../../components/admin/LoginSlideshow';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Check if user exists in admin_users table
      if (data.user) {
        const { data: adminUser, error: adminCheckError } = await supabase
          .from('admin_users')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();

        // If user doesn't exist in admin_users, they're not an admin
        if (adminCheckError || !adminUser) {
          await supabase.auth.signOut();
          throw new Error('Unauthorized: Not an admin user');
        }

        // Update last_login for admin user
        await supabase
          .from('admin_users')
          .update({
            last_login: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', data.user.id);
      }

      navigate('/kh-admin');
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A382A] via-[#1F2B1F] to-[#2A382A] flex items-center justify-center p-4">
      <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Image */}
        <div className="hidden lg:block relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-br from-[#2A382A]/80 via-[#1F2B1F]/80 to-[#2A382A]/80"></div>
          <LoginSlideshow />
          <div className="absolute inset-0 flex flex-col justify-between p-8">
            <div>
              <Link 
                to="/" 
                aria-label="Return to main website"
                className="inline-flex items-center gap-3 text-white hover:opacity-80 transition-opacity"
              >
                <img src="/assets/logo-inversed-new.webp" alt="SF Admin" className="w-8 h-8" />
                <span className="font-barrio text-xl">SF ADMIN</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto flex flex-col justify-center p-8 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/assets/logo-inversed-new.webp" alt="SF Admin" className="w-8 h-8" />
            <span className="font-barrio text-xl text-white">SF ADMIN</span>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
          <p className="text-white/70 mb-8">Please enter your credentials to continue.</p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30"
                  placeholder="Enter your password"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-500/10 text-red-500 p-4 rounded-xl text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E85C0D] text-white py-3 px-4 rounded-xl hover:bg-[#ff6a0d] transition-all duration-300 disabled:opacity-50 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>

              <Link
                to="/"
                aria-label="Return to main website"
                className="block text-center text-white/50 hover:text-white transition-colors text-sm"
              >
                Back to website
              </Link>
            </form>
        </div>
      </div>
    </div>
  );
};

export default Login;